import type { ArticleBase, CategoryBase, Product } from '@maestro/data-layer'

export type SearchFacetTypes =
  | 'conjunctive'
  | 'disjunctive'
  | 'slider'
  | 'categories'

export interface SearchFacetConfig {
  attribute: string
  label: string
  type: SearchFacetTypes
  searchable: boolean
  seoUrl: string
}

export interface SortOption {
  key: string
  attribute: string
  order: 'asc' | 'desc'
  label: string
}

export interface SearchConfig {
  facets: SearchFacetConfig[]
  sorting: SortOption[]
}

export interface SearchQuery {
  text?: string
  type?: string
  categorySearchFilters?: string
  filters?: string
  facetFilters?: string[] | (string | string[])[]
  page?: number
  resultsPerPage?: number
  isPLPBasedPage?: boolean
  isCategoryPage?: boolean
  isBrandPage?: boolean
  isCampaignPage?: boolean
  isCampaignsOverviewPage?: boolean
  sortBy?: string
  userToken?: string
  // Support for dynamic hierarchical category key (from theme.filter.categoryFilter.hierarchicalCategoryFacetKey)
  [key: string]: unknown
}

export interface SearchSuggestion {
  query: string
  highlight?: string
}

export interface SearchSuggestions<T> {
  suggestions?: T[]
  resultCount: number
}

export interface ProductSearchParams {
  resultsPerPage?: number
  filters?: string
}

export interface ProductSearchPage {
  hits: Product[]
  resultCount: number
}

export interface SearchFacetValue {
  name: string
  count: number
  selected: boolean
}

export interface SearchFacet {
  name: string
  values: SearchFacetValue[]
  selectedCount?: number
  range?: [number, number]
  selected?: boolean
  label?: string
  type?: SearchFacetTypes
}

export enum SearchEntityType {
  product = 'product',
  category = 'category',
  store = 'store',
  article = 'article',
}

export interface SearchSingleFacetStats {
  min?: number
  max?: number
  avg?: number
  sum?: number
}

export type SearchFacetStats = Record<string, SearchSingleFacetStats>

export interface SearchResults<T> {
  hits?: T[]
  indexName?: string
  resultCount: number
  resultsPerPage: number
  pageCurrent: number
  pageCount: number
  query: SearchQuery
  config: SearchConfig
  facets: SearchFacet[]
  type: SearchEntityType
  facetsStats: SearchFacetStats
  facetsStatsPerFacet?: Record<string, SearchSingleFacetStats>
  sortOptions: SortOption[]
  categoryId?: string
  queryID?: string
}

export interface SearchResultsResponse<T> {
  isLoading?: boolean
  error?: string
  entities: Map<SearchEntityType, SearchResults<T>>
}

export interface SearchCategory extends CategoryBase {
  level: number
  productCount: number
}

export interface SearchResultsEntities<T> {
  entities: Map<SearchEntityType, SearchResults<T>>
}

export interface SearchResultsBasic<T> {
  resultCount: number
  pageCount: number
  hits: T[]
  categoryId: string
  query: {
    text: string
  }
}

export interface FiltersData {
  config: SearchConfig
  facets: SearchFacet[]
  query: SearchQuery
  resultCount: number
  sliderStats: {
    name: string
    current: SearchSingleFacetStats
    default: SearchSingleFacetStats | undefined
  }[]
  sortOptions: SortOption[]
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SearchArticle extends ArticleBase {
  // add search specific fields here
}
